import React from 'react'
import { HSection } from '@s4l/ds'
import type { ListOfAncillary } from '@s4l/stayforlong/booking/ListOfAncillary'
import type { ListOfBanner } from '@s4l/stayforlong/marketing/ListOfBanner'
import type { ListOfTopDestination } from '@s4l/stayforlong/marketing/ListOfTopDestination'
import type { ListOfProperty } from '@s4l/stayforlong/property/ListOfProperty'
import {
  AboutUs,
  Advantages,
  Banner,
  LastSeen,
  Reviews,
  SubscriptionBox,
  TopDestinations,
  TrustPilotWidget,
} from '@s4l/uikit'
import Footer from 'client/components/Footer'
import Header from 'client/components/Header'
import Head from 'client/pages/Home/Head'
import BuilderModal from 'src/comps/BuilderModal'
import Maze from 'src/comps/Maze'
import Page from 'src/comps/Page'
import { routes } from 'src/constants/routes'
import { SUBSCRIPTION_BOX_HOME_PAGE } from 'src/constants/subscriptionSources'
import { SearchProvider } from 'src/context/search'
import { TopDestinationsProvider } from 'src/context/topDestinations'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { Search } from 'src/pages/homeV2/Search'
import styles from 'src/pages/homeV2/styles.module.css'
import type { ConfigType } from 'src/types/config'
import type { Destination } from 'src/types/topDestinations'

export { getServerSidePropsHandler as getServerSideProps } from 'src/pages/homeV2/server'

const pageId = routes.home.page

interface IHomeV2Props {
  readonly config: ConfigType
  readonly amazingDestinations?: Destination[]
  readonly topDestinationsJSON?: ReturnType<ListOfTopDestination['toJSON']>
  readonly bannersJSON?: ReturnType<ListOfBanner['toJSON']>
  readonly lastSeenJSON?: ReturnType<ListOfProperty['toJSON']>
  readonly ancillariesJSON: ReturnType<ListOfAncillary['toJSON']>
}

function HomeV2({
  config,
  amazingDestinations,
  topDestinationsJSON,
  bannersJSON,
  lastSeenJSON,
  ancillariesJSON,
}: IHomeV2Props) {
  const { sendEvent: sendTopDestinationEvent } = useKafkaEvent({ page: routes.home.page })
  const { sendEvent: sendClickBannerEvent } = useKafkaEvent({
    page: routes.home.page,
    eventName: 'click',
  })

  const { sendEvent: sendKafkaClick } = useKafkaEvent({ page: pageId })

  const onPropertyClick = (propertyId: string) => {
    sendKafkaClick({ section: SECTIONS.HOTELS_SEEN }, { id_property: propertyId })
  }

  return (
    <Page config={config} pageId={pageId}>
      <Head />
      <Maze />
      <SearchProvider location={null}>
        <TopDestinationsProvider value={{ destinations: amazingDestinations || [] }}>
          <div className={styles.homeContainer} data-testid="home-container">
            <Banner.Announcement bannersJSON={bannersJSON} data-testid="topbar-section" />
            <Header pageId={pageId} hasLogin ancillariesJSON={ancillariesJSON} />
            <Banner.Hero bannersJSON={bannersJSON}>
              <Search />

              <div className={styles.trustpilot}>
                <TrustPilotWidget theme="dark" />
              </div>
            </Banner.Hero>
            {lastSeenJSON && (
              <section className={styles.hotelsSeenSection} data-testid="hotels-seen-section">
                <div className={styles.sectionContainer}>
                  <LastSeen
                    lastSeenPropertiesJSON={lastSeenJSON}
                    onPropertyClick={onPropertyClick}
                  />
                </div>
              </section>
            )}

            <Advantages />
            {topDestinationsJSON && (
              <HSection
                justify="center"
                paddingTop="var(--size-40)"
                paddingBottom="var(--size-40)"
                paddingLeftRight="var(--size-16)"
              >
                <TopDestinations
                  topDestinationsJSON={topDestinationsJSON}
                  onDestinationClick={extraPayload =>
                    sendTopDestinationEvent({ section: SECTIONS.TOP_DESTINATIONS }, extraPayload)
                  }
                />
              </HSection>
            )}
            <Banner.Block
              bannersJSON={bannersJSON}
              data-testid="release-banner-section"
              onClick={() => sendClickBannerEvent({ section: SECTIONS.BANNER_BLOCK })}
            />
            <section className={styles.reviewsSection} data-testid="reviews-section">
              <div className={styles.sectionContainer}>
                <Reviews />
              </div>
            </section>
            <section className={styles.aboutUsSection} data-testid="aboutus-wrapper">
              <div className={styles.sectionContainer}>
                <AboutUs />
              </div>
            </section>
            <SubscriptionBox source={SUBSCRIPTION_BOX_HOME_PAGE} />
            <Footer />
          </div>
        </TopDestinationsProvider>
      </SearchProvider>
      <BuilderModal isSubscriptionModalActive />
    </Page>
  )
}

HomeV2.pageId = pageId
HomeV2.pagePathView = '/homeV2'

export default HomeV2
